export type TimelineGranularity = "day" | "week";

export interface TimelineColumn {
  /** Prvi dan koji stupac pokriva. */
  start: Date;
  /** Zadnji dan koji stupac pokriva; kod dnevne podjele isti kao `start`. */
  end: Date;
  label: string;
}

export interface BarPlacement {
  startIndex: number;
  span: number;
}

const MS_PER_DAY = 86_400_000;

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);

  return next;
};

/** Ponedjeljak tjedna u kojem je zadani datum — tjedan počinje ponedjeljkom, kao u kalendaru. */
const startOfWeek = (date: Date) => {
  const day = date.getDay();

  return addDays(startOfDay(date), day === 0 ? -6 : 1 - day);
};

/**
 * Stupci vremenske crte za zadani raspon.
 *
 * Kod tjedne podjele prvi stupac počinje ponedjeljkom svog tjedna, pa može zahvatiti i
 * dane prije `from` — inače bi se tjedni razilazili s kalendarom.
 */
export const buildTimelineColumns = (
  from: Date,
  to: Date,
  granularity: TimelineGranularity
): TimelineColumn[] => {
  const columns: TimelineColumn[] = [];
  const last = startOfDay(to);

  let cursor = granularity === "week" ? startOfWeek(from) : startOfDay(from);

  while (cursor <= last) {
    const end = granularity === "week" ? addDays(cursor, 6) : cursor;

    columns.push({
      start: cursor,
      end,
      label:
        granularity === "week"
          ? `${cursor.getDate()}.${cursor.getMonth() + 1}.`
          : String(cursor.getDate()),
    });

    cursor = addDays(cursor, granularity === "week" ? 7 : 1);
  }

  return columns;
};

/**
 * Položaj trake u mreži stupaca, ili `null` kad traka uopće ne dodiruje prikazani raspon.
 *
 * Traka koja izlazi izvan raspona se odreže na rub — korisnik i dalje vidi da postoji, samo
 * ne i koliko se proteže izvan prikaza.
 */
export const getBarPlacement = (
  start: Date,
  end: Date,
  columns: TimelineColumn[]
): BarPlacement | null => {
  if (!columns.length) return null;

  const barStart = startOfDay(start);
  const barEnd = startOfDay(end);

  const rangeStart = columns[0].start;
  const rangeEnd = columns[columns.length - 1].end;

  if (barEnd < rangeStart || barStart > rangeEnd) return null;

  const startIndex = columns.findIndex((column) => barStart <= column.end);
  const lastIndex = columns.reduce(
    (found, column, index) => (column.start <= barEnd ? index : found),
    0
  );

  return { startIndex, span: lastIndex - startIndex + 1 };
};

/**
 * Razvrstava trake jednog vozila u vodoravne staze tako da se dvije trake nikad ne preklope.
 *
 * Vraća indeks staze za svaku traku, istim redoslijedom kao ulaz. Traka ide u prvu stazu u
 * kojoj ima mjesta, pa se retci ne šire više nego što stvarno treba.
 */
export const assignLanes = (placements: BarPlacement[]): number[] => {
  /** Prvi slobodan stupac po stazi. */
  const laneEnds: number[] = [];

  return placements.map((placement) => {
    const lane = laneEnds.findIndex((end) => end <= placement.startIndex);
    const index = lane >= 0 ? lane : laneEnds.length;

    laneEnds[index] = placement.startIndex + placement.span;

    return index;
  });
};

/** Broj dana koje traka pokriva, uključivo — koristi se za oznaku "N dana". */
export const getBarDayCount = (start: Date, end: Date) =>
  Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / MS_PER_DAY) + 1;
